import React, { useState } from 'react';
import {
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Grid,
  Column,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Modal,
  Stack,
  InlineNotification,
} from '@carbon/react';
import { Calculator, Time, Education, Currency, Home, ArrowRight } from '@carbon/icons-react';
import { ROICalculator } from './ROICalculator';
import { TimeSavingsCalculator } from './TimeSavingsCalculator';
import { StudentOutcomeCalculator } from './StudentOutcomeCalculator';
import { CostBenefitCalculator } from './CostBenefitCalculator';
import type { CalculatorLeadCapture } from '../../../../shared/interfaces/EducationalCalculators';

interface EducationalCalculatorsProps {
  variant?: 'standard' | 'wizard' | 'interactive';
  onLeadCapture?: (lead: CalculatorLeadCapture) => Promise<void>;
}

export const EducationalCalculators: React.FC<EducationalCalculatorsProps> = ({
  variant = 'standard',
  onLeadCapture,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [capturedLeads, setCapturedLeads] = useState<CalculatorLeadCapture[]>([]);
  const [isProcessingLead, setIsProcessingLead] = useState(false);

  const handleLeadCapture = async (lead: CalculatorLeadCapture) => {
    setIsProcessingLead(true);
    try {
      // Call the API endpoint to save lead
      if (onLeadCapture) {
        await onLeadCapture(lead);
      } else {
        // Default behavior - would call API
        const response = await fetch('/api/calculators/capture-lead', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(lead),
        });

        if (!response.ok) {
          throw new Error('Failed to capture lead');
        }
      }

      setCapturedLeads(prev => [...prev, lead]);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error capturing lead:', error);
    } finally {
      setIsProcessingLead(false);
    }
  };

  const calculatorTabs = [
    {
      label: 'ROI Calculator',
      icon: Calculator,
      description: 'Calculate your return on investment',
    },
    {
      label: 'Time Savings',
      icon: Time,
      description: 'See how much time educators can save',
    },
    {
      label: 'Student Outcomes',
      icon: Education,
      description: 'Predict student improvement',
    },
    {
      label: 'Cost-Benefit',
      icon: Currency,
      description: 'Analyze costs vs benefits',
    },
  ];

  return (
    <div className="educational-calculators-page">
      <Grid>
        <Column lg={16} md={8} sm={4}>
          <Stack gap={6}>
            {/* Breadcrumb Navigation */}
            <Breadcrumb>
              <BreadcrumbItem>
                <a href="/">
                  <Home size={16} />
                </a>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <a href="/resources">Resources</a>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>Educational Calculators</BreadcrumbItem>
            </Breadcrumb>

            {/* Page Header */}
            <div className="page-header">
              <h1>Educational Value Calculators</h1>
              <p className="page-description">
                Use our interactive calculators to understand the value and impact of implementing our AI-powered
                educational platform for your institution, classroom, or family.
              </p>
            </div>

            {/* Success Notification for Captured Leads */}
            {capturedLeads.length > 0 && (
              <InlineNotification
                kind="info"
                title="Progress Saved"
                subtitle={`We've saved your calculations. ${capturedLeads.length} calculator${capturedLeads.length > 1 ? 's' : ''} completed.`}
                hideCloseButton
              />
            )}

            {/* Calculator Tabs */}
            <Tabs selectedIndex={selectedTab} onChange={({ selectedIndex }) => setSelectedTab(selectedIndex || 0)}>
              <TabList aria-label="Educational Calculators">
                {calculatorTabs.map((tab, index) => (
                  <Tab key={index}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <tab.icon size={16} />
                      <span>{tab.label}</span>
                    </div>
                  </Tab>
                ))}
              </TabList>

              <TabPanels>
                <TabPanel>
                  <Stack gap={4}>
                    <p className="tab-description">{calculatorTabs[0].description}</p>
                    <ROICalculator onLeadCapture={handleLeadCapture} variant={variant} />
                  </Stack>
                </TabPanel>

                <TabPanel>
                  <Stack gap={4}>
                    <p className="tab-description">{calculatorTabs[1].description}</p>
                    <TimeSavingsCalculator onComplete={results => {}} />
                  </Stack>
                </TabPanel>

                <TabPanel>
                  <Stack gap={4}>
                    <p className="tab-description">{calculatorTabs[2].description}</p>
                    <StudentOutcomeCalculator onComplete={results => {}} />
                  </Stack>
                </TabPanel>

                <TabPanel>
                  <Stack gap={4}>
                    <p className="tab-description">{calculatorTabs[3].description}</p>
                    <CostBenefitCalculator onComplete={results => {}} />
                  </Stack>
                </TabPanel>
              </TabPanels>
            </Tabs>

            {/* Call to Action */}
            <div
              className="calculator-cta"
              style={{
                padding: '2rem',
                backgroundColor: '#f4f4f4',
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              <h3>Ready to Transform Education?</h3>
              <p style={{ margin: '1rem 0' }}>
                Our calculators show the potential impact. Let us show you how it works in practice.
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1.5rem' }}>
                <Button kind="primary" renderIcon={ArrowRight}>
                  Schedule a Demo
                </Button>
                <Button kind="secondary">Download Full Report</Button>
              </div>
            </div>
          </Stack>
        </Column>
      </Grid>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onRequestClose={() => setShowSuccessModal(false)}
        modalHeading="Thank You!"
        primaryButtonText="Continue Exploring"
        secondaryButtonText="Schedule Demo"
        onRequestSubmit={() => setShowSuccessModal(false)}
        onSecondarySubmit={() => {
          setShowSuccessModal(false);
          // Navigate to demo scheduling
          window.location.href = '/schedule-demo';
        }}
      >
        <Stack gap={4}>
          <p>
            Thank you for using our educational calculators! Your results have been saved and our team will reach out to
            you shortly with personalized insights.
          </p>
          {capturedLeads.length > 0 && capturedLeads[capturedLeads.length - 1].interestedInDemo && (
            <InlineNotification
              kind="success"
              title="Demo Request Received"
              subtitle="We'll contact you within 24 hours to schedule your personalized demo."
              hideCloseButton
            />
          )}
          <p>
            In the meantime, feel free to explore our other calculators or download a comprehensive report of your
            results.
          </p>
        </Stack>
      </Modal>
    </div>
  );
};

export default EducationalCalculators;
